import { useEffect } from 'react';
import { Hero, SocialProof, Testimonials1, Problem } from './components/HeaderSections';
import { ProductTomos, Bonuses, UrgencyCounter, Pricing } from './components/CoreSections';
import { Testimonials2, Guarantee, FAQ, CostOfNotHaving, FinalCTA, Footer } from './components/FooterSections';
import { AnnouncementBar } from './components/AnnouncementBar';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { SocialProofPopup } from './components/SocialProofPopup';
import { ExitIntentModal } from './components/ExitIntentModal';
import { MetaPixel } from './components/MetaPixel';
// Helper: ensure a <meta> tag with given attributes exists in <head>
const ensureMeta = (attrs: Record<string, string>) => {
  if (typeof document === 'undefined') return;
  const selector = Object.entries(attrs).filter(([k]) => k !== 'content').map(([k, v]) => `[${k}="${v}"]`).join('');
  let meta = document.head.querySelector<HTMLMetaElement>(`meta${selector}`);
  if (!meta) {
    meta = document.createElement('meta');
    document.head.appendChild(meta);
  }
  Object.entries(attrs).forEach(([k, v]) => meta!.setAttribute(k, v));
};
// Helper: ensure a <link> tag exists in <head>
const ensureLink = (attrs: Record<string, string>) => {
  if (typeof document === 'undefined') return;
  const selector = `link[rel="${attrs.rel}"][href="${attrs.href}"]`;
  if (document.head.querySelector(selector)) return;
  const link = document.createElement('link');
  Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
  document.head.appendChild(link);
};
export function App() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    // Language + translation blocking (French - France)
    document.documentElement.lang = 'fr-FR';
    document.documentElement.setAttribute('translate', 'no');
    document.title = 'FITNESS FONCTIONNEL 365 — Toute votre année de cours, prête';
    ensureMeta({
      'http-equiv': 'content-language',
      content: 'fr-FR'
    });
    ensureMeta({
      name: 'google',
      content: 'notranslate'
    });
    ensureMeta({
      name: 'language',
      content: 'French'
    });
    ensureMeta({
      name: 'description',
      content: 'FITNESS FONCTIONNEL 365 : 6 Tomes, 365 séances, 5 bonus stratégiques. Toute votre année de cours de fitness fonctionnel, prête dès aujourd’hui pour 19 €. Paiement sécurisé, sans frais cachés.'
    });
    ensureMeta({
      name: 'theme-color',
      content: '#020617'
    });
    ensureMeta({
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, viewport-fit=cover'
    });
    // Performance: preconnect to Google Fonts (faster font load)
    ensureLink({
      rel: 'preconnect',
      href: 'https://fonts.googleapis.com'
    });
    ensureLink({
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossorigin: ''
    });
    // Performance: preconnect to the CDN serving the hero mockup
    ensureLink({
      rel: 'preconnect',
      href: 'https://cdn.magicpatterns.com'
    });
    ensureLink({
      rel: 'preload',
      as: 'image',
      href: 'https://cdn.magicpatterns.com/uploads/mmxQLTUbFeKMXmGcHhTPx9/MOCKUP_PRINCIPAL.png',
      // @ts-ignore
      fetchpriority: 'high'
    });
    // Performance: warm up the checkout connection so CTA clicks navigate
    // instantly. TCP + TLS happens in the background while the user reads.
    ensureLink({
      rel: 'dns-prefetch',
      href: 'https://fitness-fonctionnel-365.impultienda.ar'
    });
    // Page navigations use a non-CORS connection, so no `crossorigin` here —
    // otherwise the warmed-up connection would be ignored by the checkout.
    ensureLink({
      rel: 'preconnect',
      href: 'https://fitness-fonctionnel-365.impultienda.ar'
    });
    // Once the page is idle, quietly prefetch the checkout page so it opens
    // almost instantly when the visitor taps a CTA.
    const prefetchCheckout = () => ensureLink({
      rel: 'prefetch',
      href: 'https://fitness-fonctionnel-365.impultienda.ar/checkout'
    });
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: {
        timeout: number;
      }) => number;
    };
    if (w.requestIdleCallback) w.requestIdleCallback(prefetchCheckout, {
      timeout: 4000
    });else window.setTimeout(prefetchCheckout, 3000);
    // Performance: open the pixel connection early
    ensureLink({
      rel: 'preconnect',
      href: 'https://connect.facebook.net'
    });
  }, []);
  return <div lang="fr-FR" translate="no" className="notranslate min-h-screen bg-slate-950 font-sans text-slate-300 selection:bg-sky-400 selection:text-slate-950" data-id="element-0" data-mp="14y85wl4sigq5">
      <MetaPixel data-id="element-1" data-mp="vx06fzqjf9uh" />
      <SocialProofPopup data-id="element-2" data-mp="vvgqybcph2tr" />
      <ExitIntentModal data-id="element-3" data-mp="vybbs3zmspnp" />
      <StickyMobileCTA data-id="element-4" data-mp="3ko2oef2y8kqe" />
      <AnnouncementBar data-id="element-5" data-mp="3kob4efudc65t" />
      <Hero data-id="element-6" data-mp="3kpm8kgrq7fxg" />
      <SocialProof data-id="element-7" data-mp="3kpdw2pzimcms" />
      <Testimonials1 data-id="element-8" data-mp="3klq3bt1w9u94" />
      <Problem data-id="element-9" data-mp="3gly1dz70amcq" />
      <ProductTomos data-id="element-10" data-mp="3glehicxspnw2" />
      <Bonuses data-id="element-11" data-mp="3gkwdgu4w65dl" />
      <UrgencyCounter data-id="element-12" data-mp="3gok7rvi5uvtd" />
      <Pricing data-id="element-13" data-mp="1w4f4urx4rjp6" />
      <Testimonials2 data-id="element-14" data-mp="1w4yk20vebtzi" />
      <Guarantee data-id="element-15" data-mp="1w5ghh0tsoe90" />
      <FAQ data-id="element-16" data-mp="1w5zuc34l8cg6" />
      <CostOfNotHaving data-id="element-17" data-mp="1w2c35amm824m" />
      <FinalCTA data-id="element-18" data-mp="2060du26jndu3" />
      <Footer data-id="element-19" data-mp="205iid7rph3n8" />
    </div>;
}